import { Button, CircularProgress, Link, Menu, MenuItem, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import React, { ChangeEvent } from "react";
import { FormLine } from "./FormLine";

/**
 * Props for the DocumentUploadLine component
 */
interface DocUploadProps {
	onChange: Function;
	fl: FormLine;
}

/**
 * Component for handling document/file uploads for DOC and DOCFILE type codes.
 * Supports both desktop file selection and mobile gallery/file picking.
 */
export default function DocumentUploadLine(props: DocUploadProps) {
	const [uploading, setUploading] = React.useState(false);
	const [fileName, setFileName] = React.useState<string | null>(null);
	const [error, setError] = React.useState<string | null>(null);
	const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	/**
	 * Handles file selection from the input element.
	 * Uploads the file to the server via the staff session proxy.
	 * @param event - The change event from the file input
	 */
	const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;

		const file = files[0];
		setFileName(file.name);
		setError(null);
		setUploading(true);

		try {
			// Get the people_id from the current form context
			const currentForm = (window as any).currentForm;
			const peopleId = currentForm?.parentValue || currentForm?.peopleId;

			if (!peopleId) {
				throw new Error("Unable to determine people_id for file upload");
			}

			// Create FormData to send the file
			const formData = new FormData();
			formData.append("file", file);
			formData.append("people_id", peopleId);
			formData.append("fileName", file.name);

			// Upload via our backend proxy endpoint
			const response = await fetch("form/UploadDocument", {
				method: "POST",
				body: formData,
			});

			if (!response.ok) {
				throw new Error(`Upload failed: ${response.statusText}`);
			}

			const result = await response.json();

			// Update the form line value with the pictures_id from the response
			props.fl.value = result.pictures_id;
			
			// Store the file name in metadata for display
			if (!props.fl.metadata) {
				props.fl.metadata = {};
			}
			props.fl.metadata.file_name = result.file_name;
			
			props.onChange();
		} catch (err: any) {
			console.error("File upload error:", err);
			setError(err.message || "Failed to upload file");
			setFileName(null);
		} finally {
			setUploading(false);
			// Reset the input so the same file can be selected again if needed
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	/**
	 * Triggers the hidden file input click event
	 */
	const triggerFileSelect = () => {
		if (props.fl.isModifiable && fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	/**
	 * Opens the file in a new tab via the Picture/document endpoint
	 */
	const openFile = () => {
		if (props.fl.value) {
			window.open(`Picture/document/${props.fl.value}`, "_blank");
		}
	};

	/**
	 * Handles click on the file link - shows menu if modifiable, opens file if not
	 */
	const handleLinkClick = (event: React.MouseEvent<HTMLElement>) => {
		if (!props.fl.value) return;
		
		if (props.fl.isModifiable) {
			// Show menu with options
			setMenuAnchor(event.currentTarget);
		} else {
			// Open file directly
			openFile();
		}
	};

	/**
	 * Closes the context menu
	 */
	const handleMenuClose = () => {
		setMenuAnchor(null);
	};

	/**
	 * Handles "Upload New" menu option
	 */
	const handleUploadNew = () => {
		handleMenuClose();
		triggerFileSelect();
	};

	/**
	 * Handles "Open File" menu option
	 */
	const handleOpenFile = () => {
		handleMenuClose();
		openFile();
	};

	return (
		<>
			{/* Hidden file input - accepts all file types, allows camera/gallery on mobile */}
			<input
				ref={fileInputRef}
				type="file"
				accept="*/*"
				capture="environment"
				style={{ display: "none" }}
				onChange={handleFileSelect}
				disabled={!props.fl.isModifiable || uploading}
			/>

			{/* Upload button/link display */}
			{!props.fl.value && !uploading && (
				<Button
					variant="outlined"
					startIcon={<CloudUploadIcon />}
					onClick={triggerFileSelect}
					disabled={!props.fl.isModifiable}
					sx={{ textTransform: "none" }}
				>
					{props.fl.isModifiable ? "Select File" : "No File"}
				</Button>
			)}

			{/* Loading indicator during upload */}
			{uploading && (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<CircularProgress size={20} />
					<Typography variant="body2">Uploading {fileName}...</Typography>
				</div>
			)}

			{/* Display uploaded file info */}
			{props.fl.value && !uploading && (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<Link
						onClick={handleLinkClick}
						sx={{ cursor: "pointer" }}
					>
						{props.fl.metadata?.file_name || "Document uploaded"}
					</Link>
				</div>
			)}

			{/* Context menu for modifiable files - Upload New or Open File */}
			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={handleMenuClose}
			>
				<MenuItem onClick={handleUploadNew}>Upload New</MenuItem>
				<MenuItem onClick={handleOpenFile}>Open File</MenuItem>
			</Menu>

			{/* Error display */}
			{error && (
				<Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
					{error}
				</Typography>
			)}
		</>
	);
}
